/**
 * React Query hooks for fields.
 * Contains query hooks for fetching field data.
 *
 * For mutations, see useFieldMutations.ts
 * For cache helpers, see fieldCacheHelpers.ts
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import { processFieldResponse } from "common/utils/helpers";
import { getAllFieldGeomsFromFields } from "model/fieldHelpers";
import { Field, FieldResponse } from "model/fieldTypes";
import { useFarm } from "model/useFarmQueries";
import { useMemo } from "react";
import { useAppStore } from "stores/useAppStore";

const fieldApi = new API<FieldResponse>("fields", "v2");

// Polling interval when waiting for tiles to be generated (2 seconds)
const TILE_POLLING_INTERVAL = 2000;

// Fields are pre-fetched during initialization and rarely change.
// Use a long staleTime to prevent unnecessary re-fetches when components mount.
const FIELD_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * React Query hook for fetching a single field by guid
 * @param guid - The field guid
 * @param enabled - Whether the query should be enabled (default: true)
 */
export function useField(guid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.fields.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("Field guid is required");
      }
      const response = await fieldApi.getOne(guid);
      const processed = processFieldResponse([response]);
      return processed[0];
    },
    enabled: enabled && !!guid,
    staleTime: FIELD_STALE_TIME,
    // Poll every 2 seconds if field has no tiles (tile generation pending)
    refetchInterval: (query) => {
      const field = query.state.data;
      if (field && (!field.tiles || field.tiles.length === 0)) {
        return TILE_POLLING_INTERVAL;
      }
      return false;
    },
  });
}

/**
 * React Query hook for fetching all fields for a farm
 * Uses the farm.fields array from the current farm to fetch individual fields
 * @param farmGuid - The farm guid (optional, will use current farm if not provided)
 */
export function useFields(farmGuid?: string) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const farmId = farmGuid || currentFarmId;

  // Use reactive useFarm hook to subscribe to farm data updates
  const { data: farm } = useFarm(farmId ?? undefined);

  // Get field IDs from farm
  const fieldIds = farm?.fields || [];

  // Use useQueries to create individual queries for each field
  const fieldQueries = useQueries({
    queries: fieldIds.map((fieldId) => ({
      queryKey: queryKeys.fields.detail(fieldId),
      queryFn: async () => {
        const response = await fieldApi.getOne(fieldId);
        const processed = processFieldResponse([response]);
        return processed[0];
      },
      enabled: !!farmId && fieldIds.length > 0,
      staleTime: FIELD_STALE_TIME,
      // Poll every 2 seconds if field has no tiles (tile generation pending)
      refetchInterval: (query: { state: { data: Field | undefined } }) => {
        const field = query.state.data;
        if (field && (!field.tiles || field.tiles.length === 0)) {
          return TILE_POLLING_INTERVAL;
        }
        return false;
      },
    })),
  });

  // Extract data and status from queries - memoize to avoid creating new arrays on every render
  const fields = useMemo(
    () =>
      fieldQueries
        .map((query) => query.data)
        .filter((field): field is Field => field !== undefined),
    [fieldQueries]
  );

  const isLoading = fieldQueries.some((query) => query.isLoading);
  const isError = fieldQueries.some((query) => query.isError);
  const errors = useMemo(
    () =>
      fieldQueries
        .map((query) => query.error)
        .filter((error) => error !== null),
    [fieldQueries]
  );

  // Memoize the return object to prevent unnecessary re-renders in consumers
  return useMemo(
    () => ({
      data: fields,
      isLoading,
      isError,
      errors,
      isSuccess: !isLoading && !isError,
      // Return individual query refs for granular access if needed
      queries: fieldQueries,
    }),
    [fields, isLoading, isError, errors, fieldQueries]
  );
}

/**
 * React Query hook for fetching multiple fields by their guids
 * @param fieldIds - Array of field guids
 * @param enabled - Whether the query should be enabled (default: true)
 */
export function useFieldsByIds(fieldIds: string[] | undefined, enabled = true) {
  const queries = useQueries({
    queries: (fieldIds || []).map((fieldId) => ({
      queryKey: queryKeys.fields.detail(fieldId),
      queryFn: async () => {
        const response = await fieldApi.getOne(fieldId);
        const processed = processFieldResponse([response]);
        return processed[0];
      },
      enabled: enabled && fieldIds !== undefined && fieldIds.length > 0,
    })),
  });

  const fields = queries
    .map((query) => query.data)
    .filter((field): field is Field => field !== undefined);

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    data: fields,
    isLoading,
    isError,
    queries,
  };
}

/**
 * React Query hook to get all field geometries
 * Fetches all fields and computes their geometries as a FeatureCollection
 * @param farmGuid - The farm guid (optional, will use current farm if not provided)
 */
export function useAllFieldGeoms(farmGuid?: string) {
  const { data: fields = [], isLoading, isError } = useFields(farmGuid);

  const fieldGeoms = useMemo(() => {
    return getAllFieldGeomsFromFields(fields);
  }, [fields]);

  return {
    data: fieldGeoms,
    isLoading,
    isError,
  };
}

// Re-export mutations from useFieldMutations.ts
export {
  useCreateField,
  useDeleteField,
  useUpdateField,
} from "./useFieldMutations";

// Re-export cache helpers from fieldCacheHelpers.ts
export {
  changeFieldVisibilityQuery,
  fetchFieldsByIdQuery,
  invalidateFieldDataQuery,
  moveIntoFieldQuery,
  optimisticFieldMoveIn,
  resetFieldsQueryCache,
  useChangeFieldVisibilityQuery,
  useFetchFieldsByIdQuery,
  useInvalidateFieldDataQuery,
  useResetFieldsQueryCache,
  useValidateFieldDataQuery,
  validateFieldDataQuery,
} from "./fieldCacheHelpers";

// Export the API instance for direct use if needed
export { fieldApi };
